/**
 * MazeAppOverlay.tsx
 * 「迷路」アプリのメイン画面。おしゃべりアプリ(ChatAppOverlay)と同じくフルスクリーンの
 * オーバーレイとして表示する。編集モード（フロー作成）と進行モード（実行）を切り替える。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Provider, useSetAtom } from 'jotai';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import { unlockSpeechAudioContext } from '@/speech/speechAudioContext';
import { Clock3, Flag, Languages, Pencil, Play, RotateCcw, Square, X } from 'lucide-react';
import { runFlow } from '@/flow/run';
import { LogPanel } from '@/flow/components/LogPanel';
import { VirtualRobot } from '@/flow/components/VirtualRobot';
import { getMazeStage } from '@/flow/config/mazeStages';
import { setFlowMotionAtom } from '@/flow/atoms/flowMotionAtom';
import { setFlowSayTextAtom } from '@/flow/atoms/flowSayTextAtom';
import { setFlowTofAtom } from '@/flow/atoms/flowTofAtom';
import type { AppEdge, AppNode, NodeKind } from '@/flow/types';
import { MazeFlowCanvas } from './MazeFlowCanvas';
import { MazeCardPalette } from './MazeCardPalette';
import { useMazeRunner } from './useMazeRunner';
import { MazeCameraModeToggle } from './MazeCameraModeToggle';
import { formatMazeElapsedTime, useMazeTimer } from './useMazeTimer';
import type { MazeCameraMode, MazeCameraRotation } from '@/flow/animation/useWegoAnimationScene';
import type { FlowDisplayLanguage } from '@/flow/displayLanguage';
import { validateFlowConnectivity } from '@/flow/validateFlowConnectivity';
import { playSimulationSoundEffect } from '@/flow/simulationSoundPlayer';

export type MazeAppOverlayProps = {
  stageId: number;
  initialDisplayLanguage?: FlowDisplayLanguage;
  onClose: () => void;
};

type MazePhase = 'edit' | 'running';

export function MazeAppOverlay(props: MazeAppOverlayProps) {
  return <Provider><MazeAppContent {...props} /></Provider>;
}

function MazeAppContent({ stageId, initialDisplayLanguage = 'ja', onClose }: MazeAppOverlayProps) {
  const { playSimulationSpeech, synthesizeSpeech } = useFiloRemote();
  const stage = getMazeStage(stageId);
  const [phase, setPhase] = useState<MazePhase>('edit');
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [addRequestKind, setAddRequestKind] = useState<NodeKind | null>(null);
  const [goalReached, setGoalReached] = useState(false);
  const [runWarning, setRunWarning] = useState<string | null>(null);
  const [displayLanguage, setDisplayLanguage] = useState<FlowDisplayLanguage>(initialDisplayLanguage);
  const [showCardNames, setShowCardNames] = useState(true);
  const [cameraMode, setCameraMode] = useState<MazeCameraMode>('free');
  const [cameraRotation, setCameraRotation] = useState<MazeCameraRotation>({ horizontal: 0, vertical: 0 });
  const [activeNodeIds, setActiveNodeIds] = useState<Set<string>>(() => new Set());
  const [blockedNodeIds, setBlockedNodeIds] = useState<Set<string>>(() => new Set());
  const [activeEdgeIds, setActiveEdgeIds] = useState<Set<string>>(() => new Set());
  const flowRef = useRef<{ nodes: AppNode[]; edges: AppEdge[] }>({ nodes: [], edges: [] });
  const abortControllerRef = useRef<AbortController | null>(null);
  const measureTofRef = useRef<(() => number) | null>(null);
  const setFlowMotion = useSetAtom(setFlowMotionAtom);
  const setSayText = useSetAtom(setFlowSayTextAtom);
  const setTofScan = useSetAtom(setFlowTofAtom);
  const { elapsedMs, start: startTimer, stop: stopTimer } = useMazeTimer();

  const log = useCallback((line: string) => {
    setLogs((current) => [...current, line]);
  }, []);

  const { mazeControl, resetPose, pose } = useMazeRunner(stage);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    resetPose();
  }, [resetPose, setFlowMotion, setSayText, setTofScan]);

  const onFlowChange = useCallback((nodes: AppNode[], edges: AppEdge[]) => {
    flowRef.current = { nodes, edges };
  }, []);

  const onTofMeasureReady = useCallback((measure: (() => number) | null) => {
    measureTofRef.current = measure;
  }, []);

  const onNodeHighlight = useCallback((nodeId: string, active: boolean) => {
    setActiveNodeIds((current) => {
      const next = new Set(current);
      if (active) next.add(nodeId);
      else next.delete(nodeId);
      return next;
    });
  }, []);

  const setNodeRuntimeState = useCallback((nodeId: string, state: 'blocked' | null) => {
    setBlockedNodeIds((current) => {
      const next = new Set(current);
      if (state === 'blocked') next.add(nodeId);
      else next.delete(nodeId);
      return next;
    });
  }, []);

  const onEdgeHighlight = useCallback((edgeId: string, active: boolean) => {
    setActiveEdgeIds((current) => {
      const next = new Set(current);
      if (active) next.add(edgeId);
      else next.delete(edgeId);
      return next;
    });
  }, []);

  const startRunning = useCallback(() => {
    const connectivityWarning = validateFlowConnectivity(
      flowRef.current.nodes,
      flowRef.current.edges,
      displayLanguage,
    );
    if (connectivityWarning) {
      setRunWarning(connectivityWarning);
      setLogs([connectivityWarning]);
      return;
    }

    unlockSpeechAudioContext();
    setRunWarning(null);
    abortControllerRef.current?.abort();
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    setLogs([]);
    setGoalReached(false);
    setActiveNodeIds(new Set());
    setBlockedNodeIds(new Set());
    setActiveEdgeIds(new Set());
    resetPose();
    setPhase('running');
    setIsRunning(true);
    startTimer();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    void runFlow(
      flowRef.current.nodes,
      flowRef.current.edges,
      log,
      {
        mazeControl: { ...mazeControl, continueFlowAfterGoal: true },
        setFlowMotion,
        setSayText,
        playSimulationSpeech,
        playSimulationSound: playSimulationSoundEffect,
        synthesizeSpeech,
        setTofScan,
        setNodeRuntimeState,
        onExecutionEvent: (event) => {
          if (
            event.type !== 'terminal-reached'
            || !flowRef.current.nodes.some((node) => node.id === event.nodeId && node.type === 'end')
          ) return;
          const reached = mazeControl.isAtGoal?.() ?? false;
          setGoalReached(reached);
          log(reached
            ? '[Maze] エンドでゴール到着を確認しました'
            : '[Maze] エンドに到着しましたが、現在位置はゴールではありません');
        },
        measureSimulationTof: () => measureTofRef.current?.() || mazeControl.measureDistanceMm?.(2500) || 0,
      },
      onEdgeHighlight,
      controller.signal,
      false,
      onNodeHighlight,
      true,
    ).finally(() => {
      if (abortControllerRef.current !== controller) return;
      abortControllerRef.current = null;
      setIsRunning(false);
      stopTimer();
    });
  }, [displayLanguage, log, mazeControl, onEdgeHighlight, onNodeHighlight, playSimulationSpeech, synthesizeSpeech, resetPose, setFlowMotion, setNodeRuntimeState, setSayText, setTofScan, startTimer, stopTimer]);

  const stopRunning = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    setActiveNodeIds(new Set());
    setBlockedNodeIds(new Set());
    setActiveEdgeIds(new Set());
    setIsRunning(false);
    stopTimer();
  }, [setFlowMotion, setSayText, setTofScan, stopTimer]);

  const backToEdit = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    resetPose();
    setGoalReached(false);
    setActiveNodeIds(new Set());
    setBlockedNodeIds(new Set());
    setActiveEdgeIds(new Set());
    stopTimer();
    setIsRunning(false);
    setPhase('edit');
  }, [resetPose, setFlowMotion, setSayText, setTofScan, stopTimer]);

  return (
    <div className="maze-app-overlay" role="dialog" aria-modal="true" aria-labelledby="maze-app-title">
      <header className="maze-app-overlay__header">
        <div>
          <p className="eyebrow">FILO APP</p>
          <h2 id="maze-app-title">迷路</h2>
          <p>{stage.name}</p>
        </div>
        <div className="maze-app-overlay__header-actions">
          <span className="maze-app-timer" role="timer" aria-label={`経過時間 ${formatMazeElapsedTime(elapsedMs)}`}>
            <Clock3 aria-hidden="true" size={14} />
            経過 {formatMazeElapsedTime(elapsedMs)}
          </span>
          {goalReached && (
            <span className="maze-app-status maze-app-status--goal">
              <Flag aria-hidden="true" size={14} />
              ゴール到着！
            </span>
          )}
          <button
            type="button"
            className="maze-app-language-button"
            onClick={() => setDisplayLanguage((language) => language === 'ja' ? 'en' : 'ja')}
            aria-label={`表示言語を${displayLanguage === 'ja' ? 'English' : '日本語'}に切り替え`}
            title={displayLanguage === 'ja' ? 'Switch to English' : '日本語表示に切り替え'}
          >
            <Languages aria-hidden="true" size={15} />
            {displayLanguage === 'ja' ? 'EN' : '日本語'}
          </button>
          {phase === 'edit' ? (
            <button type="button" className="maze-app-progress-button" onClick={startRunning}>
              <Play aria-hidden="true" size={15} fill="currentColor" />
              進行
            </button>
          ) : (
            <>
              <button type="button" className="maze-app-progress-button" onClick={startRunning}>
                <RotateCcw aria-hidden="true" size={15} />
                再開
              </button>
              <button
                type="button"
                className="maze-app-progress-button maze-app-progress-button--stop"
                onClick={stopRunning}
                disabled={!isRunning}
              >
                <Square aria-hidden="true" size={14} fill="currentColor" />
                停止
              </button>
              <button type="button" className="maze-app-progress-button maze-app-progress-button--edit" onClick={backToEdit}>
                <Pencil aria-hidden="true" size={15} />
                編集にもどる
              </button>
            </>
          )}
          {phase === 'edit' && (
            <button type="button" className="chat-app-icon-button" onClick={onClose} aria-label="迷路アプリを閉じる">
              <X aria-hidden="true" size={20} />
            </button>
          )}
        </div>
      </header>
      <div className={`maze-app-overlay__body ${phase === 'running' ? 'maze-app-overlay__body--running' : 'maze-app-overlay__body--editing'}`}>
        <section className="maze-app-overlay__panel maze-app-overlay__panel--flow" aria-label="フローチャート">
          <h3 className="maze-panel__title">フローチャート</h3>
          <div className="maze-app-overlay__flow-canvas">
            <MazeFlowCanvas
              displayLanguage={displayLanguage}
              stageId={stageId}
              onFlowChange={onFlowChange}
              addRequestKind={addRequestKind}
              onAddHandled={() => setAddRequestKind(null)}
              activeNodeIds={activeNodeIds}
              blockedNodeIds={blockedNodeIds}
              activeEdgeIds={activeEdgeIds}
            />
          </div>
          {phase === 'edit' && (
            <div className={`maze-card-dock${showCardNames ? '' : ' is-icon-only'}`}>
              <MazeCardPalette
                onTapAdd={setAddRequestKind}
                displayLanguage={displayLanguage}
                showCardNames={showCardNames}
                onToggleCardNames={() => setShowCardNames((visible) => !visible)}
              />
            </div>
          )}
          {phase === 'running' && (
            <div className="maze-app-overlay__log-strip">
              <LogPanel logs={logs} />
            </div>
          )}
        </section>
        <section className="maze-app-overlay__panel maze-app-overlay__panel--simulator" aria-label="シミュレーター">
          <div className="maze-simulator-toolbar">
            <h3 className="maze-panel__title">シミュレーター</h3>
            <MazeCameraModeToggle value={cameraMode} onChange={setCameraMode} />
          </div>
          {cameraMode === 'free' && (
            <div className="maze-camera-sliders" aria-label="フリーカメラ位置">
              <label>
                <span>左右</span>
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={cameraRotation.horizontal}
                  aria-label="カメラを左右に回転"
                  onChange={(event) => setCameraRotation((current) => ({
                    ...current,
                    horizontal: Number(event.target.value),
                  }))}
                />
              </label>
              <label>
                <span>上下</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={cameraRotation.vertical}
                  aria-label="カメラを上下に回転"
                  onChange={(event) => setCameraRotation((current) => ({
                    ...current,
                    vertical: Number(event.target.value),
                  }))}
                />
              </label>
            </div>
          )}
          <VirtualRobot
            mazeStage={stage}
            mazeRobotPose={pose}
            topView
            cameraMode={cameraMode}
            mazeCameraRotation={cameraRotation}
            onTofMeasureReady={onTofMeasureReady}
          />
        </section>
      </div>
      {runWarning && (
        <div role="alert" className="absolute bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-xl">
          <span>{runWarning}</span>
          <button
            type="button"
            onClick={() => setRunWarning(null)}
            aria-label="警告を閉じる"
            className="text-red-100 hover:text-white"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
